import { BrowserRouter, Routes, Route, Link, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Onboarding from './pages/Onboarding';
import Tenants from './pages/Tenants';
import Templates from './pages/Templates';
import Users from './pages/Users';
import Groups from './pages/Groups';
import Assignments from './pages/Assignments';
import Admins from './pages/Admins';
import Licenses from './pages/Licenses';
import AdminConsentSuccess from './pages/AdminConsentSuccess';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Support from './pages/Support';
import Features from './pages/Features';
import SetupWizard from './pages/SetupWizard';
import LicenseExpired from './pages/LicenseExpired';
import Demo from './pages/Demo';
import { ToastProvider } from './components/Toast';

function App() {
  // Check if user is authenticated via cookie (set by backend)
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'https://sign.virtue.dk';
        const response = await fetch(`${apiUrl}/api/auth/me`, {
          credentials: 'include', // Send cookies
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setIsAuthenticated(true);
            setIsAdmin(data.data.isAdmin || false);
          } else {
            setIsAuthenticated(false);
            setIsAdmin(false);
          }
        } else {
          setIsAuthenticated(false);
          setIsAdmin(false);
        }
      } catch (error) {
        setIsAuthenticated(false);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <ToastProvider>
      <BrowserRouter>
        <Routes>
        {/* Public routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/admin-consent-success" element={<AdminConsentSuccess />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/support" element={<Support />} />
        <Route path="/features" element={<Features />} />
        <Route path="/demo" element={<Demo />} />
        <Route path="/license-expired" element={<LicenseExpired />} />
        <Route path="/setup" element={isAuthenticated ? <SetupWizard /> : <Navigate to="/login" />} />
        
        {/* Protected routes */}
        <Route path="/dashboard/*" element={isAuthenticated ? <DashboardLayout isAdmin={isAdmin} /> : <Navigate to="/login" />}>
          <Route index element={isAdmin ? <Dashboard /> : <NotAdmin />} />
          <Route path="tenants" element={isAdmin ? <Tenants /> : <NotAdmin />} />
          <Route path="templates" element={isAdmin ? <Templates /> : <NotAdmin />} />
          <Route path="users" element={isAdmin ? <Users /> : <NotAdmin />} />
          <Route path="groups" element={isAdmin ? <Groups /> : <NotAdmin />} />
          <Route path="assignments" element={isAdmin ? <Assignments /> : <NotAdmin />} />
          <Route path="admins" element={isAdmin ? <Admins /> : <NotAdmin />} />
          <Route path="licenses" element={isAdmin ? <Licenses /> : <NotAdmin />} />
        </Route>
        </Routes>
      </BrowserRouter>
    </ToastProvider>
  );
}

function NotAdmin() {
  return (
    <div style={{
      background: 'white',
      padding: '40px',
      borderRadius: '12px',
      textAlign: 'center',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    }}>
      <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔒</div>
      <h2 style={{ margin: '0 0 10px', color: '#1f2937' }}>Admin Access Required</h2>
      <p style={{ color: '#6b7280', marginBottom: '20px' }}>
        You need admin privileges to access the dashboard. Please contact your administrator.
      </p>
    </div>
  );
}

function DashboardLayout({ isAdmin }: { isAdmin: boolean }) {
  const [stats, setStats] = useState<any>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const handleSendConsentLink = async () => {
    if (!adminEmail || !adminEmail.includes('@')) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setSendingEmail(true);
    setEmailError('');
    
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://sign.virtue.dk';
      const response = await fetch(`${apiUrl}/api/auth/send-consent-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ adminEmail })
      });

      if (response.ok) {
        setEmailSent(true);
        setTimeout(() => {
          setShowEmailModal(false);
          setEmailSent(false);
          setAdminEmail('');
        }, 3000);
      } else {
        const data = await response.json();
        setEmailError(data.error || 'Failed to send email');
      }
    } catch (err) {
      setEmailError('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  useEffect(() => {
    const apiUrl = import.meta.env.VITE_API_URL || 'https://sign.virtue.dk';
    
    // Get current user's tenant and fetch stats for consent warning
    fetch(`${apiUrl}/api/auth/me`, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          return fetch(`${apiUrl}/api/dashboard/stats/${data.data.tenantId}`, {
            credentials: 'include',
          });
        }
      })
      .then((res) => res?.json())
      .then((data) => {
        if (data?.success) {
          setStats(data.data);
        }
      })
      .catch((error) => {
        console.error('Failed to load stats for consent check:', error);
      });
  }, []);

  const handleLogout = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://sign.virtue.dk';
      await fetch(`${apiUrl}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } finally {
      window.location.href = '/login';
    }
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'system-ui', minHeight: '100vh', background: '#f9fafb' }}>
      <header style={{
        marginBottom: '40px',
        background: 'white',
        padding: '15px 20px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
          <div style={{ minWidth: '200px' }}>
            <h1 style={{ margin: 0, fontSize: '20px' }}>🖊️ VirtueSign</h1>
            <p style={{ color: '#666', margin: '5px 0 0', fontSize: '13px' }}>Email Signature Management</p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            {stats?.health.needsAdminConsent && (
              <>
                <button
                  onClick={() => {
                    const apiUrl = import.meta.env.VITE_API_URL || 'https://sign.virtue.dk';
                    window.location.href = `${apiUrl}/api/auth/consent`;
                  }}
                  style={{
                    background: '#fef3c7',
                    color: '#92400e',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    border: '1px solid #fbbf24',
                    cursor: 'pointer',
                  }}
                >
                  <span>⚠️</span>
                  <span style={{ whiteSpace: 'nowrap' }}>Grant Consent</span>
                </button>
                <button
                  onClick={() => setShowEmailModal(true)}
                  style={{
                    background: 'white',
                    color: '#92400e',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    border: '1px solid #fbbf24',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Send to Admin
                </button>
              </>
            )}
            <button
              onClick={handleLogout}
              style={{
                background: '#ef4444',
                color: 'white',
                padding: '8px 16px',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '14px',
                whiteSpace: 'nowrap',
              }}
            >
              Logout
            </button>
          </div>
        </div>
        <nav style={{ marginTop: '15px', display: 'flex', gap: '15px', overflowX: 'auto', paddingBottom: '5px' }}>
          <Link to="/dashboard" style={{ color: '#667eea', textDecoration: 'none', fontWeight: '500', whiteSpace: 'nowrap', fontSize: '14px' }}>Dashboard</Link>
          <Link to="/dashboard/templates" style={{ color: '#667eea', textDecoration: 'none', fontWeight: '500', whiteSpace: 'nowrap', fontSize: '14px' }}>Templates</Link>
          <Link to="/dashboard/users" style={{ color: '#667eea', textDecoration: 'none', fontWeight: '500', whiteSpace: 'nowrap', fontSize: '14px' }}>Users</Link>
          <Link to="/dashboard/assignments" style={{ color: '#667eea', textDecoration: 'none', fontWeight: '500', whiteSpace: 'nowrap', fontSize: '14px' }}>Assignments</Link>
          <Link to="/dashboard/admins" style={{ color: '#667eea', textDecoration: 'none', fontWeight: '500', whiteSpace: 'nowrap', fontSize: '14px' }}>Admins</Link>
          <Link to="/dashboard/licenses" style={{ color: '#667eea', textDecoration: 'none', fontWeight: '500', whiteSpace: 'nowrap', fontSize: '14px' }}>Licenses</Link>
        </nav>
      </header>

      <main>
        <Routes>
          <Route index element={isAdmin ? <Dashboard /> : <NotAdmin />} />
          <Route path="templates" element={isAdmin ? <Templates /> : <NotAdmin />} />
          <Route path="users" element={isAdmin ? <Users /> : <NotAdmin />} />
          <Route path="assignments" element={isAdmin ? <Assignments /> : <NotAdmin />} />
          <Route path="admins" element={isAdmin ? <Admins /> : <NotAdmin />} />
          <Route path="licenses" element={isAdmin ? <Licenses /> : <NotAdmin />} />
        </Routes>
      </main>

      {/* Email Modal */}
      {showEmailModal && (
        <div
          onClick={() => setShowEmailModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '500px',
              width: '90%',
            }}
          >
            {emailSent ? (
              <>
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '15px' }}>✓</div>
                  <h3 style={{ margin: 0, color: '#1f2937' }}>Email Sent!</h3>
                  <p style={{ color: '#6b7280', marginTop: '10px' }}>
                    Admin consent link sent to {adminEmail}
                  </p>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ margin: '0 0 20px', color: '#1f2937' }}>Send Consent Link to Admin</h3>
                <p style={{ color: '#6b7280', marginBottom: '20px' }}>
                  Enter the email address of a Global Administrator who can grant consent.
                </p>
                <input
                  type="email"
                  value={adminEmail}
                  onChange={(e) => {
                    setAdminEmail(e.target.value);
                    setEmailError('');
                  }}
                  placeholder="admin@company.com"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: emailError ? '1px solid #ef4444' : '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '16px',
                    marginBottom: '10px',
                  }}
                />
                {emailError && (
                  <p style={{ color: '#ef4444', fontSize: '14px', margin: '0 0 20px' }}>{emailError}</p>
                )}
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button
                    onClick={handleSendConsentLink}
                    disabled={sendingEmail}
                    style={{
                      flex: 1,
                      background: sendingEmail ? '#9ca3af' : '#667eea',
                      color: 'white',
                      padding: '12px',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: sendingEmail ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {sendingEmail ? 'Sending...' : 'Send Email'}
                  </button>
                  <button
                    onClick={() => setShowEmailModal(false)}
                    style={{
                      padding: '12px 24px',
                      background: 'transparent',
                      color: '#6b7280',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Dashboard() {
  const [tenantId, setTenantId] = useState<string>('');
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const apiUrl = import.meta.env.VITE_API_URL || 'https://sign.virtue.dk';
    
    // Get current user's tenant
    fetch(`${apiUrl}/api/auth/me`, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setTenantId(data.data.tenantId);
          // Fetch stats
          return fetch(`${apiUrl}/api/dashboard/stats/${data.data.tenantId}`, { credentials: 'include' });
        }
      })
      .then((response) => {
        if (!response) return;
        return response.json();
      })
      .then((data) => {
        if (data?.success) setStats(data.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Failed to load dashboard:', error);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
        Loading dashboard...
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ margin: '0 0 10px' }}>Dashboard</h2>
        <p style={{ color: '#666', margin: 0 }}>Overview of your email signature system</p>
      </div>

      {/* Overview Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '30px' }}>
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>Total Users</div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#1f2937' }}>{stats?.overview.totalUsers || 0}</div>
        </div>
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>Total Groups</div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#667eea' }}>{stats?.overview.totalGroups || 0}</div>
        </div>
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>Active Templates</div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#059669' }}>{stats?.overview.activeTemplates || 0}</div>
        </div>
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>Template Assignments</div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#f59e0b' }}>{stats?.overview.totalAssignments || 0}</div>
        </div>
      </div>

      {/* Outlook Add-in Installation */}
      <div style={{ marginBottom: '30px' }}>
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 10px', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>📥</span>
            <span>Deploy Outlook Add-in</span>
          </h3>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 20px' }}>
            Download the manifest file and deploy it to your Microsoft 365 organization to enable automatic signature insertion.
          </p>
          
          {/* Download Button */}
          <a
            href="https://sign.virtue.dk/addin/manifest.xml"
            download="virtuesign-manifest.xml"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: '#667eea',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '6px',
              textDecoration: 'none',
              fontWeight: '500',
              fontSize: '14px',
              marginBottom: '20px',
            }}
          >
            <span>⬇️</span>
            <span>Download Manifest File</span>
          </a>

          {/* Installation Instructions */}
          <div style={{ marginTop: '20px', padding: '15px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
            <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: '600' }}>Installation Steps:</h4>
            <ol style={{ margin: 0, paddingLeft: '20px', color: '#374151', fontSize: '13px', lineHeight: '1.8' }}>
              <li>
                <strong>Go to Microsoft 365 Admin Center:</strong> <a href="https://admin.microsoft.com" target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>admin.microsoft.com</a>
              </li>
              <li>
                Navigate to <strong>Settings</strong> → <strong>Integrated apps</strong>
              </li>
              <li>
                Click <strong>"Upload custom apps"</strong>
              </li>
              <li>
                Choose <strong>"Office Add-in"</strong> (not Teams app or other types)
              </li>
              <li>
                Select <strong>"Upload manifest file (.xml)"</strong>
              </li>
              <li>
                Upload the downloaded <code style={{ background: '#e5e7eb', padding: '2px 6px', borderRadius: '3px', fontSize: '12px' }}>virtuesign-manifest.xml</code> file
              </li>
              <li>
                Choose deployment options:
                <ul style={{ marginTop: '4px', paddingLeft: '20px' }}>
                  <li><strong>Entire organization</strong> - Deploy to all users</li>
                  <li><strong>Specific users/groups</strong> - Deploy to selected users</li>
                </ul>
              </li>
              <li>
                Click <strong>"Deploy"</strong> and confirm
              </li>
              <li>
                Users will see "VirtueSign" in their Outlook ribbon within 24 hours (or after restarting Outlook)
              </li>
            </ol>
            
            <div style={{ marginTop: '15px', padding: '12px', background: '#dbeafe', borderRadius: '4px', fontSize: '13px', color: '#1e40af' }}>
              <strong>💡 Tip:</strong> For immediate testing, users can manually install the add-in by going to <strong>Outlook</strong> → <strong>Get Add-ins</strong> → <strong>My add-ins</strong> → <strong>Add from File</strong>
            </div>
            
            <div style={{ marginTop: '12px', fontSize: '12px', color: '#6b7280' }}>
              <strong>Need help?</strong> Visit the <a href="https://learn.microsoft.com/en-us/microsoft-365/admin/manage/deploy-add-ins" target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>Microsoft documentation</a> for detailed deployment instructions.
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div style={{ marginBottom: '30px' }}>
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 15px', fontSize: '16px', fontWeight: '600' }}>Recent Activity</h3>
          <div style={{ display: 'flex', gap: '40px' }}>
            <div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>Last User Sync</div>
              <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                {stats?.activity.lastUserSync ? new Date(stats.activity.lastUserSync).toLocaleString() : 'Never'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>Last Group Sync</div>
              <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                {stats?.activity.lastGroupSync ? new Date(stats.activity.lastGroupSync).toLocaleString() : 'Never'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
